import { type ElementStyles, css, html } from '@microsoft/fast-element';
import type { HoverIconDefinition, HoverIcon } from './HoverIcon';
import { FolderArrowRightRegular, FolderArrowRightFilled } from '@fluentui/react-icons/fonts';
import { moveTo } from 'owa-locstrings/lib/strings/moveto.locstring.json';
import { MoveMenu } from '../../components/MoveMenu';
import { DirectionalHint } from '@fluentui/react';
import {
    getMoveToPropertiesForContextMenu,
    lazyGetMoveToPropertiesForContextMenu,
} from 'owa-mail-movetofolder-view';
import { getMailMenuItemShouldShow } from 'owa-mail-filterable-menu-behavior';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';
import { tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault } from 'owa-mail-list-store';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { getFolderIdOrUnifiedInboxFolderId } from 'owa-unified-inbox-data-utils/lib/getFolderIdOrUnifiedInboxFolderId';
import type { MailboxType } from 'owa-graph-schema';
import { lazyMoveMailListRows } from 'owa-mail-triage-action';
import { type LoadMoreFoldersActionSource } from 'owa-folders-constants';
import { lazyShowMoveToFolderDialogV9 } from 'owa-mail-folder-moveto-dialog';
import { MoveToFolderScenario } from 'owa-mail-moveto-control-types';
import React from 'react';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Need to create a new root since the web component is not part of React
import { type Root, createRoot } from 'react-dom/client';

interface MoveToHoverIcon extends HoverIcon {
    renderRoot: Root | undefined;
}

const MoveRegularFontGlyph = FolderArrowRightRegular.codepoint;
const MoveFilledFontGlyph = FolderArrowRightFilled.codepoint;

const BaseMoveIconStyles: ElementStyles = css`
    .icon:before {
        color: var(--colorNeutralForeground3);
        content: '${MoveRegularFontGlyph}';
    }

    :host(:hover) .icon:before,
    :host(:active) .icon:before {
        color: var(--customBlueForeground1);
        content: '${MoveFilledFontGlyph}';
    }
`;

async function renderContextMenu(this: Readonly<MoveToHoverIcon>, open: boolean) {
    // Trigger load of lazyGetMoveToPropertiesForContextMenu or the first open will be empty
    await lazyGetMoveToPropertiesForContextMenu.import();
    this.renderRoot?.render(
        React.createElement(MoveMenu, {
            menuProps: getMoveToPropertiesForContextMenu(
                this.tableViewId,
                this.mailboxInfo,
                () => renderContextMenu.call(this, false),
                'Hover',
                getMailMenuItemShouldShow,
                DirectionalHint.bottomLeftEdge,
                [this.rowKey]
            ),
            open,
            onMenuClose: () => renderContextMenu.call(this, false),
            target: this.iconRef,
        })
    );
}

export const Move: HoverIconDefinition<MoveToHoverIcon> = {
    title(this: Readonly<MoveToHoverIcon>) {
        return moveTo;
    },
    customCss: BaseMoveIconStyles,
    onClick(this: Readonly<MoveToHoverIcon>) {
        if (isUnifiedInboxMailboxInfo(this.mailboxInfo)) {
            // Get the actual mailboxInfo and folderId of the item to move
            const unifiedInboxFolderId = this.tableView?.tableQuery?.folderId;
            const actualMailboxInfo = tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault(
                getModuleContextMailboxInfo(),
                [this.rowKey],
                this.tableView
            );
            const actualSourceFolderId = getFolderIdOrUnifiedInboxFolderId(
                unifiedInboxFolderId,
                actualMailboxInfo
            );

            const selectorProps = {
                sourceFolderId: actualSourceFolderId,
                supportedMailBoxTypes: [
                    'UserMailbox',
                    'ArchiveMailbox',
                    'SharedMailbox',
                ] as MailboxType[],
                onFolderClick: (destinationFolderId: string) => {
                    lazyMoveMailListRows.importAndExecute(
                        [this.rowKey],
                        destinationFolderId,
                        this.tableViewId,
                        'Hover'
                    );
                },
                excludeSearchFolders: true,
                excludeNotesFolder: true,
                includeAllAccounts: true,
                loadMoreActionSource: 'ContextMenu',
            };
            lazyShowMoveToFolderDialogV9.importAndExecute({
                targetWindow: window,
                mailboxInfo: actualMailboxInfo,
                scenario: MoveToFolderScenario.MoveTo,
                source: 'HoverAction',
                selectorProps: { ...selectorProps },
            });
        } else {
            renderContextMenu.call(this, true);
        }
    },
    customTemplate: html`<span id="renderroot"></span>`,
    connectedCallback(this: MoveToHoverIcon) {
        const container = this.shadowRoot?.querySelector('#renderroot');
        if (container) {
            this.renderRoot = createRoot(container);
        }
    },
    disconnectedCallback(this: MoveToHoverIcon) {
        this.renderRoot?.unmount();
        this.renderRoot = undefined;
    },
};
